// src/services/usersApi.js
import axios from 'axios';
import {getJwtToken, saveUserData} from '../utils/auth';
import {getBaseUrl} from "../utils/apiClient";

const usersApiClient = axios.create({
    baseURL: `${getBaseUrl()}/users`,
});

usersApiClient.interceptors.request.use(async (config) => {
    const token = await getJwtToken(); // Espera a que se obtenga el token
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    //config.headers['bypass-tunnel-reminder'] = 'true';
    return config;
}, (error) => {
    return Promise.reject(error); // Si hay un error, lo propagamos
});

export const registerUser = async (userData) => {
    try {
        const response = await usersApiClient.post('', userData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getUserInfo = async (email) => {
    try {
        const response = await usersApiClient.get(`/${email}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getLoggedUser = async () => {
    try {
        const response = await usersApiClient.get();
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const editUserInfo = async (user) => {
    try {
        const response = await usersApiClient.put(`/${user.email}`, user);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const addFavoriteProvider = async (providerId) => {
    try {
        const response = await usersApiClient.put(`/addFavorite/${providerId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const removeFavoriteProvider = async (providerId) => {
    try {
        const response = await usersApiClient.put(`/removeFavorite/${providerId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const login = async () => {
    try {
        const response = await usersApiClient.get('/login');
        saveUserData(response.data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const glogin = async (data) => {
    try {
        const response = await usersApiClient.post('/glogin', data);
        saveUserData(response.data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const updateLocation = async (locationPayload) => {
    try {
        const response = await usersApiClient.put('/location', locationPayload);
        return response.data;
    } catch (error) {
        console.error('❌ updateLocation API - Error:', error);
        throw error;
    }
}

export const deleteUser = async (email) => {
    try {
        const response = await usersApiClient.delete(`/${email}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const duplicateUserWithLowercase = async (email) => {
    try {
        const response = await usersApiClient.post(`/admin/duplicate-lowercase/${email}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const deleteSelf = async () => {
    try {
        const response = await usersApiClient.delete(`/delete`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const countUsers = async () => {
    try {
        const response = await usersApiClient.get('/count');
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const listUsers = async () => {
    try {
        const response = await usersApiClient.get('/list-all');
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Agregar estos métodos al final del archivo usersApi.js, antes del export default

export const getNotifications = async (pagesize = 10, lastDocumentId = null) => {
    try {
        const response = await usersApiClient.get('/notifications', {
            params: {pagesize, lastDocumentId},
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getUnreadNotifications = async (pagesize = 10, lastDocumentId = null) => {
    try {
        const response = await usersApiClient.get('/notifications/unread', {
            params: {pagesize, lastDocumentId},
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const getUnseenNotifications = async (pagesize = 5, lastDocumentId = null) => {
    try {
        const response = await usersApiClient.get('/notifications/unseen', {
            params: {pagesize, lastDocumentId},
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const markNotificationAsRead = async (notifId) => {
    try {
        const response = await usersApiClient.put(`/notification/${notifId}/read`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const markNotificationAsUnread = async (notifId) => {
    try {
        const response = await usersApiClient.put(`/notification/${notifId}/unread`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const markNotificationAsSeen = async (notifId) => {
    try {
        const response = await usersApiClient.put(`/notification/${notifId}/seen`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const markNotificationAsUnseen = async (notifId) => {
    try {
        const response = await usersApiClient.put(`/notification/${notifId}/unseen`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const markAllNotificationsAsSeen = async () => {
    try {
        const response = await usersApiClient.put('/notifications/mark-all-seen');
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const markAllNotificationsAsRead = async () => {
    try {
        const response = await usersApiClient.put('/notifications/mark-all-read');
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Función auxiliar para construir el payload de ubicación según tu backend SmartLocation
export const buildLocationPayload = (displayName, coordinates = null, source = 'MANUAL', serviceLocationId = null, isApproximate = false) => {
    const payload = {
        displayName,
        source: source.toUpperCase(), // Tu backend usa ENUM: GPS, APPROXIMATE, ZONE_SELECTION, CUSTOM_INPUT, MANUAL
        isApproximate
    };

    // Si tiene coordenadas, agregarlas con la estructura que espera tu backend
    if (coordinates && coordinates.latitude && coordinates.longitude) {
        payload.coordinates = {
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
            accuracy: coordinates.accuracy || null
        };
    }

    // Si tiene serviceLocationId, agregarlo
    if (serviceLocationId) {
        payload.serviceLocationId = serviceLocationId;
    }

    return payload;
};

export default usersApiClient;
